import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSocketConnection, useSocketEvent } from './socket';
import { t } from '../../i18n';

/**
 * Turns server events into things a person notices.
 *
 * Two rules keep this from becoming an annoyance:
 *
 *  1. **Refresh quietly, interrupt rarely.** Most events only invalidate a
 *     query — the number on screen changes and nothing demands attention. A
 *     toast is reserved for something a person has to act on.
 *  2. **Never render the payload as truth.** An event says *that* something
 *     happened; the refreshed query says *what*. A socket message is the one
 *     input to this app that did not come back through an authorised fetch.
 */

interface BroadcastPayload {
  id?: string;
  title?: string;
  body?: string;
  priority?: string;
}

/**
 * Trims and caps anything from the wire before it reaches the screen.
 *
 * Exported for the test: this is the only place in the console where a string
 * reaches a person without having come back through an authorised fetch, so a
 * non-string, an over-long string and an empty one all have to be handled.
 */
export function safeText(value: unknown, max: number): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : '';
}

export function useLiveUpdates(): void {
  useSocketConnection();

  const queryClient = useQueryClient();

  const invalidate = React.useCallback(
    (...keys: string[]) => {
      for (const key of keys) void queryClient.invalidateQueries({ queryKey: [key] });
    },
    [queryClient],
  );

  // ── A platform-wide notice ───────────────────────────────────────────────
  useSocketEvent<BroadcastPayload>('platform.broadcast', (payload) => {
    const title = safeText(payload.title, 120);
    if (!title) return;

    const body = safeText(payload.body, 300);
    const urgent = payload.priority === 'CRITICAL' || payload.priority === 'HIGH';

    // Kept on screen until dismissed when it is urgent: planned downtime is
    // not something to miss because it faded after four seconds.
    const options = { description: body || undefined, duration: urgent ? Infinity : 8000 };
    if (urgent) toast.warning(title, options);
    else toast.info(title, options);
  });

  // ── The bell ─────────────────────────────────────────────────────────────
  useSocketEvent('notification.created', () => {
    invalidate('notifications', 'sidebar-counts');
  });

  // ── Someone is waiting on the committee ──────────────────────────────────
  useSocketEvent<{ flatLabel?: string }>('member.pending_approval', (payload) => {
    invalidate('members', 'sidebar-counts');
    const flat = safeText(payload.flatLabel, 24);
    toast.info(t('A resident is waiting for approval'), {
      description: flat ? `They have claimed ${flat}.` : undefined,
    });
  });

  useSocketEvent('complaint.created', () => {
    invalidate('complaints', 'sidebar-counts', 'dashboard');
  });

  useSocketEvent('complaint.updated', () => {
    invalidate('complaints', 'dashboard');
  });

  // ── Money ────────────────────────────────────────────────────────────────
  useSocketEvent('payment.received', () => {
    // No toast. During a bill run this fires dozens of times an hour, and a
    // treasurer watching the collections total wants the number to move, not a
    // stack of notifications to dismiss.
    invalidate('payments', 'bills', 'dashboard', 'accounting');
  });

  // ── The gate ─────────────────────────────────────────────────────────────
  useSocketEvent('sos.triggered', () => {
    invalidate('sos', 'dashboard');
    toast.error(t('An SOS has been raised'), {
      description: t('Open Visitors → SOS for the details.'),
      duration: Infinity,
    });
  });

  useSocketEvent('visitor_pass.created', () => invalidate('visitors'));
  useSocketEvent('announcement.published', () => invalidate('announcements', 'dashboard'));
  useSocketEvent('booking.confirmed', () => invalidate('amenities', 'amenity-bookings'));
  useSocketEvent('booking.cancelled', () => invalidate('amenities', 'amenity-bookings'));
}
